import { UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { PerkWizardFormData } from "./PerkWizard";

interface SettingsStepProps {
  form: UseFormReturn<PerkWizardFormData>;
}

export function SettingsStep({ form }: SettingsStepProps) {
  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="capFrequency"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Cap Frequency</FormLabel>
            <Select onValueChange={field.onChange} value={field.value || "Once"}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select cap frequency" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Once">Once (lifetime)</SelectItem>
                <SelectItem value="Daily">Daily</SelectItem>
                <SelectItem value="Weekly">Weekly</SelectItem>
                <SelectItem value="Monthly">Monthly</SelectItem>
                <SelectItem value="Yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription>
              How often an employee can redeem this perk.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="maxRedemptionsPerEmployee"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Max Redemptions Per Employee (Optional)</FormLabel>
            <FormControl>
              <Input
                type="number"
                min={1}
                placeholder="Leave empty for unlimited"
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
              />
            </FormControl>
            <FormDescription>
              Maximum number of times a single employee can redeem this perk.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="maxRedemptionsTotal"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Max Total Redemptions (Optional)</FormLabel>
            <FormControl>
              <Input
                type="number"
                min={1}
                placeholder="Leave empty for unlimited"
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
              />
            </FormControl>
            <FormDescription>
              Maximum total redemptions across all employees. Perk becomes sold out after this limit.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="soldOutBehavior"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Sold Out Behavior</FormLabel>
            <Select onValueChange={field.onChange} value={field.value || "ShowBadge"}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select behavior" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="ShowBadge">Show "Sold Out" Badge</SelectItem>
                <SelectItem value="HideFromListing">Hide from Listing</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription>
              What happens when all promo codes or total redemptions are used up.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="sortOrder"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Sort Order (Optional)</FormLabel>
            <FormControl>
              <Input
                type="number"
                min={0}
                placeholder="0"
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
              />
            </FormControl>
            <FormDescription>
              Lower numbers appear first. Default is 0.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
